import Scrollspy from 'react-scrollspy-ez';
import styled from 'styled-components';

import BadgeStyled from '../../../components/Badge';
import ContentHeaderStyled from '../../../components/ContentHeader';
import ExternalLinkGroupStyled from '../../../components/ExternalLinkGroup';
import PaginationStyled from '../../../components/Pagination';
import { REFERENCE_LINKS } from '../../../constants';
import { IconDanger, IconSuccess } from '../../../core';
import commonPageContentStyles from '../../../styles/mixins/_commonPageContentStyles';
import navigationStyles from '../../../styles/mixins/navigationMenuStyles';

interface Props {
  className?: string;
}

const dataExternalLinkGroups = [
  {
    id: 1,
    title: 'Government usability testing resources',
    cards: [
      {
        title: 'IBM Web Accessibility Checklist',
        subtitle: '[ibm.com]',
        linkUrl: REFERENCE_LINKS.ibmAccessibilityChecklist,
      },
      {
        title: 'Government usability testing resources',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovUsabilityTesting,
      },
      {
        title: 'Testing for plain-language usability',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovPlainLanguageGuide,
      },
    ],
  },
  {
    id: 2,
    title: 'Government accessibility resources',
    cards: [
      {
        title: 'The Section 508 accessibility program',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508Program,
      },
      {
        title: 'Accessibility for Teams guide',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovAccessibilityForTeams,
      },
      {
        title: 'Section 508 Coordinators GitHub',
        subtitle: '[github.com]',
        linkUrl: REFERENCE_LINKS.section508CoordinatorsGithub,
      },
      {
        title: '18F’s accessibility guide',
        subtitle: '[guides.18f.org]',
        linkUrl: REFERENCE_LINKS.eighteenFAccessibilityGuide,
      },
      {
        title: 'Accessibility Requirements Tool (ART) for contracting',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508AccessibilityRequirementsTool,
      },
      {
        title: 'Understanding universal design',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508UniversalDesign,
      },
    ],
  },
  {
    id: 3,
    title: 'Non-government accessibility resources',
    cards: [
      {
        title: 'Web Accessibility Perspectives Videos',
        subtitle: '[w3.org]',
        linkUrl: REFERENCE_LINKS.w3cPerspectiveVideos,
      },
      {
        title: 'Inclusive design principles',
        subtitle: '[inclusivedesignprinciples.info]',
        linkUrl: REFERENCE_LINKS.inclusiveDesignPrinciples,
      },
      {
        title: 'Accessibility fundamentals with Rob Dodson',
        subtitle: '[youtube.com]',
        linkUrl: REFERENCE_LINKS.robDodsonAccessibilityFundamentals,
      },
      {
        title: 'Microsoft’s inclusive design manual',
        subtitle: '[inclusive.microsoft.design]',
        linkUrl: REFERENCE_LINKS.microsoftInclusiveDesign,
      },
      {
        title: 'Inclusive components by Heydon Pickering',
        subtitle: '[inclusive-components.design]',
        linkUrl: REFERENCE_LINKS.inclusiveComponents,
      },
      {
        title: 'Humane by design',
        subtitle: '[humanebydesign.com]',
        linkUrl: REFERENCE_LINKS.humaneByDesign,
      },
    ],
  },
];

const AccessibilityStructure = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="content">
      <ContentHeaderStyled
        title="Accessibility"
        breadcrumbs={[{ label: 'Get Started', url: '/' }, { label: 'Accessibility' }]}
      />
      <div className="page-container">
        <div className="page-content">
          <div className="page-content-with-navigation-wrapper">
            <div className="page-content-with-navigation">
              <div className="page-content-item">
                <h2 className="h1" id="overview">
                  Overview
                </h2>
                <h3 className="h3" id="definition">
                  Definition
                </h3>
                <p>
                  The Accessibility page within our design system serves as a central resource,
                  offering clear guidelines and actionable best practices to create digital
                  experiences that are inclusive and usable by individuals of all abilities. By
                  integrating accessibility into every stage of the design and development process,
                  we ensure that our products are equitable, intuitive, and enjoyable for all users,
                  regardless of their physical, sensory, or cognitive capabilities.
                </p>
                <p className="mb-40">
                  Accessibility is not just about compliance—it’s about empathy and inclusivity.
                  It’s a commitment to delivering digital solutions that respect and support the
                  diverse needs of our audience, fostering a truly accessible digital environment.
                </p>
                <h3 className="h3" id="rationale">
                  Rationale
                </h3>
                <p>
                  The Accessibility page exists to promote an inclusive design culture and ensure
                  that accessibility is a fundamental part of our digital products. By providing
                  clear guidance, principles, and tools, we help teams create solutions that are
                  usable by individuals of all abilities, ensuring no one is excluded from
                  interacting with or benefiting from our services.
                </p>
                <p>
                  This page emphasizes that accessibility is not just about meeting standards—it’s
                  about enhancing the overall experience for all users. It serves as a resource to
                  foster empathy-driven design, improve usability, and ensure consistency across all
                  products, while also supporting legal and ethical responsibilities.
                </p>
                <p className="mb-40">
                  Ultimately, the Accessibility page reinforces our commitment to designing with
                  purpose: creating digital products that are intuitive, inclusive, and impactful
                  for everyone.
                </p>
              </div>
              <h2 className="divider h1" id="guidelines">
                Guidelines
              </h2>
              <div className="page-content-item">
                <h3 className="h3" id="best-practices">
                  Best Practices
                </h3>
                <ul className="ordered-list">
                  <li>
                    <strong>Accessible Navigation and Readability:</strong> Ensure that all
                    functionality can be operated using only a keyboard, and maintain sufficient
                    color contrast between text and background to improve legibility, especially for
                    users with visual impairments.
                  </li>
                  <li>
                    <strong>Content Alternatives and Scalability:</strong> Provide descriptive
                    alternative text for images, icons, and media to support screen readers, and
                    ensure that text can be resized without losing clarity or functionality, making
                    content adaptable to user preferences.
                  </li>
                  <li>
                    <strong>Error Handling and Responsive Design:</strong> Design interfaces that
                    help users prevent and recover from errors by offering clear instructions and
                    feedback. Additionally, create responsive designs that work seamlessly across
                    different screen sizes, input methods, and devices.
                  </li>
                </ul>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-accessibility/screen-voice-search@2x.webp"
                    width={488}
                    height={336}
                    alt="Voice search"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p>
                      The label voice search describes the user task (search) paired with the input
                      method (voice).
                    </p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-accessibility/screen-microphone@2x.webp"
                    width={488}
                    height={336}
                    alt="Microphone"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p>
                      The label voice search describes the user task (search) paired with the input
                      method (voice).
                    </p>
                  </div>
                </div>
              </div>
              <div className="page-content-item">
                <div className="visually-hidden" aria-hidden="true" id="principles">
                  Principles
                </div>
                <h3 className="h3">Principles:</h3>
                <ul className="ordered-list">
                  <li>
                    <strong>Inclusivity First:</strong> Design with empathy to ensure digital
                    products are accessible to users of all abilities, fostering an environment
                    where no one is excluded.
                  </li>
                  <li>
                    <strong>Perceivable Content:</strong> Ensure that all information and interface
                    components are presented in ways that users can perceive, regardless of sensory
                    or physical limitations.
                  </li>
                  <li>
                    <strong>Operability for All:</strong> Build interfaces that can be easily
                    navigated and interacted with using various devices, including keyboards,
                    touchscreens, and assistive technologies.
                  </li>
                  <li>
                    <strong>Understandability:</strong> Create clear, consistent, and intuitive
                    experiences by using straightforward language, predictable layouts, and
                    informative feedback.
                  </li>
                  <li>
                    <strong>Robust &amp; Future-Ready:</strong> Develop products that are compatible
                    with a wide range of devices, platforms, and assistive technologies, ensuring
                    they remain accessible as technology evolves.
                  </li>
                </ul>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-accessibility/screen-bg-color-standard@2x.webp"
                    width={488}
                    height={336}
                    alt="Standard background color"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p>
                      Use a background color that has enough contrast to meet the AA standard ratio.
                    </p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-accessibility/screen-bg-color-spectrum-text@2x.webp"
                    width={488}
                    height={336}
                    alt="Text color"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p>
                      Use a background color that is close in the spectrum to the text color used.
                    </p>
                  </div>
                </div>
              </div>
              <div className="page-content-item">
                <div className="visually-hidden" aria-hidden="true" id="considerations">
                  Considerations
                </div>
                <h3 className="h3">Considerations for Diverse Abilities:</h3>
                <ul className="ordered-list">
                  <li>
                    <strong>Visual Impairments &amp; Color Vision Deficiencies:</strong> Provide
                    text alternatives for images and ensure proper color contrast. Avoid using color
                    as the sole means of conveying information to accommodate users with color
                    blindness.
                  </li>
                  <li>
                    <strong>Hearing Impairments:</strong> Include captions and transcripts for audio
                    and video content, making information accessible without relying solely on
                    sound.
                  </li>
                  <li>
                    <strong>Motor Limitations:</strong> Ensure that all functionality can be
                    accessed via keyboard and assistive devices. Use large, well-spaced interactive
                    elements to improve ease of use.
                  </li>
                  <li>
                    <strong>Cognitive &amp; Learning Disabilities:</strong> Simplify content using
                    clear language, consistent navigation, and intuitive design patterns to reduce
                    cognitive load.
                  </li>
                </ul>
              </div>
              <img
                className="mb-40 img-responsive"
                src="/images/get-started-accessibility/example-color-vision@2x.webp"
                width={1000}
                height={330}
                alt="Color Vision Deficiencies"
              />
              <div className="page-content-item">
                <div className="visually-hidden" aria-hidden="true" id="accessibility-standards">
                  Accessibility Standards
                </div>
                <h3 className="h3">Global Accessibility Standards: </h3>
                <p>
                  Our design system is built to align with global accessibility standards, including
                  the Web Content Accessibility Guidelines (WCAG), ensuring that our digital
                  products are inclusive, usable, and accessible to all users.
                </p>
              </div>
              <div className="examples-wrapper">
                <div className="examples-wrapper-item">
                  <img
                    src="/images/get-started-accessibility/this-text-is-accessible-01@2x.webp"
                    width={488}
                    height={264}
                    alt=""
                  />
                  <p>Text: Grey 20, Background: Blue 60, Gap: 40</p>
                </div>
                <div className="examples-wrapper-item">
                  <img
                    src="/images/get-started-accessibility/this-text-is-accessible-02@2x.webp"
                    width={488}
                    height={264}
                    alt=""
                  />
                  <p>Text: Blue 70 , Background: Ultramarine 30, Gap: 40</p>
                </div>
                <div className="examples-wrapper-item">
                  <img
                    src="/images/get-started-accessibility/this-text-is-accessible-03@2x.webp"
                    width={488}
                    height={264}
                    alt=""
                  />
                  <p>Text: Grey 40, Background: Blue 60, Gap: 20</p>
                </div>
                <div className="examples-wrapper-item">
                  <img
                    src="/images/get-started-accessibility/this-text-is-accessible-04@2x.webp"
                    width={488}
                    height={264}
                    alt=""
                  />
                  <p>Text: Gray 10 , Background: Ultramarine 30, Gap: 20</p>
                </div>
              </div>
              <figure>
                <img
                  src="/images/get-started-accessibility/example-of-headings-marked-up-in-code@2x.webp"
                  width={1000}
                  height={494}
                  alt="Example of headings marked up in code"
                />
                <figcaption>Example of headings marked up in code</figcaption>
              </figure>
              <div className="page-content-item">
                <h3 className="h3">Define headings</h3>
                <p className="mb-40">
                  Headings are essential for creating an organized, accessible, and user-friendly
                  experience. By defining clear, hierarchical headings, we guide users through
                  content in a logical flow. This structure not only improves navigation but also
                  ensures that assistive technologies, such as screen readers, can accurately
                  interpret and present the information. Our design system emphasizes the importance
                  of consistent and descriptive headings, making it easier for all users to find,
                  understand, and interact with content. By adhering to these practices, we foster a
                  more inclusive and intuitive digital environment for everyone.
                </p>
              </div>
              <figure>
                <img
                  src="/images/get-started-accessibility/example-accessibility-labels-marked-up-in-code@2x.webp"
                  width={1000}
                  height={455}
                  alt="Example: The accessibility labels marked up in code for these icons describe their purpose"
                />
                <figcaption>
                  Example: The accessibility labels marked up in code for these icons describe their
                  purpose.
                </figcaption>
              </figure>
              <div className="page-content-item">
                <h3 className="h3">Conclusion:</h3>
                <p className="mb-40">
                  Accessibility is at the core of our design philosophy. By following global
                  standards and best practices, we ensure that our digital products are usable by
                  everyone, regardless of ability. Through thoughtful design, clear headings, and
                  inclusive features, we create experiences that are intuitive, adaptable, and
                  accessible. We are committed to continuously improving accessibility, fostering an
                  environment where all users can interact seamlessly with our products, today and
                  in the future.
                </p>
              </div>
              <h2 className="divider h1" id="references">
                References
              </h2>
              {dataExternalLinkGroups.map((externalLinkGroup) => (
                <ExternalLinkGroupStyled
                  key={externalLinkGroup.id}
                  title={externalLinkGroup.title}
                  cards={externalLinkGroup.cards}
                />
              ))}
            </div>

            <div>
              <div className="page-navigation">
                <h2>Contents</h2>
                <Scrollspy
                  ids={[
                    'overview',
                    'definition',
                    'rationale',
                    'guidelines',
                    'best-practices',
                    'principles',
                    'considerations',
                    'accessibility-standards',
                    'references',
                  ]}
                  offset={0}
                  activeItemClassName="active"
                  itemContainerClassName="multilevel-link-list"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <PaginationStyled
        previousLink="/"
        previous="Get Started"
        nextLink="/get-started/content"
        next="Content"
      />
    </div>
  </div>
);

const Accessibility = styled(AccessibilityStructure)`
    ${commonPageContentStyles}
    ${navigationStyles([2, 3, 5, 6, 7, 8])}
    overflow: unset;

    figure figcaption {
      color: ${({ theme }) => theme.neutralTextDisabled};
    }

    height: 100%;

   
        > .content {
            width: 100%;
            padding: 0;
        }
}
`;

export default Accessibility;
